import SlimSelect from 'slim-select/dist/slimselect.es.js';
import { Controller } from "@hotwired/stimulus";
import 'slim-select/dist/slimselect.css';

// Connects to data-controller="slimselect"
export default class extends Controller {
    static values = {
        options: Object,
        submitOnClose: Boolean,
        summary: Boolean
    };

    connect() {
        this.submitPending = false;

        this.slimselect = new SlimSelect({
            select: this.element,
            ...this.optionsValue,
            events: {
                ...this.optionsValue.events,
                afterChange: this.afterChange.bind(this),
                afterClose: this.afterClose.bind(this)
            }
        });

        if (this.summaryValue) {
            this.connectSummary();
        }
    }

    disconnect() {
        this.resizeObserver?.disconnect();
        this.slimselect.destroy();
    }

    afterChange() {
        if (this.summaryValue) {
            this.renderSummary();
        }

        if (!this.submitOnCloseValue) {
            return;
        }

        // Removing a value from the closed control applies straight away, otherwise wait for
        // the dropdown to close so a run of picks only submits once.
        if (this.slimselect.settings.isOpen) {
            this.submitPending = true;
        } else {
            this.submitForm();
        }
    }

    afterClose() {
        if (!this.submitOnCloseValue || !this.submitPending) {
            return;
        }

        this.submitForm();
    }

    submitForm() {
        this.submitPending = false;
        this.element.form?.requestSubmit();
    }

    // Summary mode shows the selected options as plain comma separated text, like the value of a
    // normal select, in place of Slim Select's chips. Options that do not fit are counted in a
    // "+N" badge. The chips are hidden with CSS (.slimselect-summary) rather than rewritten,
    // because Slim Select updates them in place and would fight any changes made to them.
    connectSummary() {
        const main = this.slimselect.render.main.main;

        this.summaryText = document.createElement("span");
        this.summaryText.className = "ss-summary-text";
        this.summaryBadge = document.createElement("span");
        this.summaryBadge.className = "ss-summary-badge";

        this.summary = document.createElement("div");
        this.summary.className = "ss-summary";
        this.summary.append(this.summaryText, this.summaryBadge);
        main.insertBefore(this.summary, main.firstChild);

        this.resizeObserver = new ResizeObserver(() => this.renderSummary());
        this.resizeObserver.observe(this.summary);

        this.renderSummary();
    }

    // Shows as many labels as fit, dropping from the end into the badge. If even the first label
    // does not fit it is cut off with an ellipsis.
    renderSummary() {
        const labels = Array.from(this.element.selectedOptions)
            .filter((option) => option.value !== "")
            .map((option) => option.text);

        if (labels.length === 0) {
            this.summaryText.textContent = this.slimselect.settings.placeholderText;
            this.summaryBadge.hidden = true;
            return;
        }

        for (let shown = labels.length; shown >= 1; shown--) {
            const hidden = labels.length - shown;
            this.summaryText.textContent = labels.slice(0, shown).join(", ");
            this.summaryBadge.textContent = `+${hidden}`;
            this.summaryBadge.hidden = hidden === 0;

            if (this.summaryText.scrollWidth <= this.summaryText.clientWidth) {
                return;
            }
        }
    }
}
